import { useState } from "react";
import { DotsThree, PaperPlaneTilt } from "@phosphor-icons/react";
import { Avatar, IconButton } from "../../design-system";
import { friendlyError, invalidateQueries } from "../../analytics";
import { trackInviteMemberSent } from "../../analytics/tracking";
import {
  useCurrentUser,
  useIdentityClient,
  useOrganization,
  useOrgMembers,
  useOrgRole,
  type OrgMember,
} from "../../identity";
import { PanelSkeleton, QueryBoundary } from "../../components/dashboard";
import { Popover } from "../../components/controls/Popover";
import {
  ModalButton,
  TokenwiseModal,
} from "../../components/controls/TokenwiseModal";

function initialsOf(member: OrgMember): string {
  const source = member.name.trim() || member.email;
  const parts = source.split(/[\s@.]+/).filter(Boolean);
  return (
    parts
      .slice(0, 2)
      .map((p) => p[0])
      .join("") || "?"
  ).toUpperCase();
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
}

/* TokenWise status pill. Local to this page so it uses the redesign's tokens
 * rather than the shared DS Badge; keeps the exact labels the rest of the app
 * (and the tests) expect. */
function Pill({
  children,
  bg,
  color,
  dot,
}: {
  children: React.ReactNode;
  bg: string;
  color: string;
  dot?: boolean;
}) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold"
      style={{ background: bg, color }}
    >
      {dot && (
        <span
          aria-hidden
          className="size-1.5 rounded-full"
          style={{ background: color }}
        />
      )}
      {children}
    </span>
  );
}

function RoleBadge({ member }: { member: OrgMember }) {
  if (member.pending) {
    return (
      <Pill bg="var(--tint-amber)" color="var(--val-amber)" dot>
        Invite pending
      </Pill>
    );
  }

  if ((member.role ?? "MEMBER") === "ADMIN") {
    return (
      <Pill bg="var(--tint-crimson)" color="var(--val-crimson)">
        Admin
      </Pill>
    );
  }

  return (
    <Pill bg="var(--surface-2)" color="var(--text-muted)">
      Member
    </Pill>
  );
}

/* Organization members: who's in, their role, pending invites. Org-scoped —
 * the same list in every workspace. Admins invite, change roles and resend
 * invites; everyone else gets the read-only list. */
export default function MembersPage() {
  const client = useIdentityClient();
  const me = useCurrentUser();
  const isAdmin = useOrgRole() === "ADMIN";
  const [version, setVersion] = useState(0);
  const members = useOrgMembers(version);
  const org = useOrganization();

  const [inviting, setInviting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Evict every cached members entry (all versions) so OTHER consumers of the
  // list — e.g. the org overview's member count — refetch instead of serving
  // a pre-mutation entry from the dedupe window; the bump refetches this page.
  const refresh = () => {
    invalidateQueries("identity:members");
    setVersion((v) => v + 1);
  };

  /** Run a row action; surface failures in the shared banner. */
  const rowAction = async (action: () => Promise<unknown>) => {
    setActionError(null);
    try {
      await action();
      refresh();
    } catch (err) {
      setActionError(friendlyError(err).message);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3 py-2">
        <div className="min-w-0">
          <h1 className="tw-display text-[26px] font-semibold leading-tight text-[var(--text)]">
            Members
          </h1>
          <p className="mt-1 max-w-2xl text-[12px] text-[var(--text-muted)]">
            {org.data
              ? `People in ${org.data.name} and their access to this dashboard.`
              : "People in your organization and their access to this dashboard."}
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setInviting(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 text-[12px] font-semibold text-white transition-opacity hover:opacity-90"
            style={{ background: "#9D1D48" }}
          >
            <PaperPlaneTilt size={14} weight="fill" /> Invite members
          </button>
        )}
      </div>

      {actionError && (
        <div
          className="px-3 py-2 text-[12px]"
          style={{
            background: "var(--tint-crimson)",
            border: "1px solid var(--border-2)",
            color: "var(--val-crimson)",
          }}
        >
          {actionError}
        </div>
      )}

      <QueryBoundary state={members} skeleton={<PanelSkeleton rows={6} />}>
        {(rows) => (
          <div style={{ background: "var(--surface)" }}>
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  <Th>Member</Th>
                  <Th>Role</Th>
                  <Th>Added on</Th>
                  {isAdmin && <th className="w-12" />}
                </tr>
              </thead>
              <tbody>
                {rows.map((member, i, arr) => (
                  <MemberRow
                    key={member.id}
                    member={member}
                    isSelf={member.email === me.email}
                    isAdmin={isAdmin}
                    last={i === arr.length - 1}
                    onChangeRole={(role) =>
                      rowAction(() => client.changeRole(member.email, role))
                    }
                    onResend={() =>
                      rowAction(async () => {
                        await client.inviteMember(member.email);
                        trackInviteMemberSent("resend");
                      })
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryBoundary>

      <InviteMemberDialog
        open={inviting}
        onClose={() => setInviting(false)}
        onInvited={refresh}
      />
    </div>
  );
}

function Th({ children }: { children?: React.ReactNode }) {
  return (
    <th className="px-4 py-2.5 text-left text-[9px] font-semibold uppercase tracking-widest text-[var(--label)]">
      {children}
    </th>
  );
}

function MemberRow({
  member,
  isSelf,
  isAdmin,
  last,
  onChangeRole,
  onResend,
}: {
  member: OrgMember;
  isSelf: boolean;
  isAdmin: boolean;
  /** Skip the bottom border on the final row. */
  last: boolean;
  onChangeRole: (role: "ADMIN" | "MEMBER") => void;
  onResend: () => void;
}) {
  // No self-service role change — another admin has to do it.
  const showMenu = isAdmin && !isSelf;

  return (
    <tr
      className="transition-colors hover:bg-[var(--hover)]"
      style={last ? undefined : { borderBottom: "1px solid var(--border)" }}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <Avatar size="md" initials={initialsOf(member)} />
          <div className="min-w-0">
            <div className="truncate font-medium text-[var(--text)]">
              {member.name || member.email}
              {isSelf && (
                <span className="ml-1.5 text-[var(--text-muted)]">(you)</span>
              )}
            </div>
            {member.name && (
              <div className="truncate text-[var(--text-muted)]">
                {member.email}
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <RoleBadge member={member} />
      </td>
      <td className="px-4 py-3 text-[var(--text-muted)]">
        {formatDate(member.addedOn)}
      </td>
      {isAdmin && (
        <td className="px-2 py-3 text-right">
          {showMenu && (
            <Popover
              align="end"
              trigger={
                <IconButton
                  variant="ghost"
                  size="sm"
                  icon={<DotsThree />}
                  aria-label={`Actions for ${member.name || member.email}`}
                />
              }
            >
              {(close) => (
                <div className="flex flex-col">
                  {member.pending ? (
                    <MenuItem
                      onClick={() => {
                        close();
                        onResend();
                      }}
                    >
                      Resend invitation
                    </MenuItem>
                  ) : member.role === "ADMIN" ? (
                    <MenuItem
                      onClick={() => {
                        close();
                        onChangeRole("MEMBER");
                      }}
                    >
                      Make member
                    </MenuItem>
                  ) : (
                    <MenuItem
                      onClick={() => {
                        close();
                        onChangeRole("ADMIN");
                      }}
                    >
                      Make admin
                    </MenuItem>
                  )}
                </div>
              )}
            </Popover>
          )}
        </td>
      )}
    </tr>
  );
}

function MenuItem({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-9 items-center px-2.5 text-left text-[12px] hover:bg-[var(--hover)]"
      style={{ color: "var(--text)" }}
    >
      {children}
    </button>
  );
}

const EMAIL_RE = /^\S+@\S+\.\S+$/;

export interface ParsedEmails {
  /** Valid, de-duplicated addresses in first-seen order. */
  valid: string[];
  /** Tokens that look like an attempt at an address but don't validate. */
  invalid: string[];
}

/* Split a free-text paste (commas, semicolons, whitespace, or newlines) into
 * addresses. Dedupes case-insensitively while preserving the original casing
 * of the first occurrence; partitions the rest as invalid so the dialog can
 * point at the bad ones. */
export function parseEmails(raw: string): ParsedEmails {
  const valid: string[] = [];
  const invalid: string[] = [];
  const seen = new Set<string>();

  for (const token of raw.split(/[\s,;]+/)) {
    const email = token.trim();
    if (!email) continue;

    if (!EMAIL_RE.test(email)) {
      invalid.push(email);
      continue;
    }

    const key = email.toLowerCase();
    if (seen.has(key)) continue;

    seen.add(key);
    valid.push(email);
  }

  return { valid, invalid };
}

function InviteMemberDialog({
  open,
  onClose,
  onInvited,
}: {
  open: boolean;
  onClose: () => void;
  onInvited: () => void;
}) {
  const client = useIdentityClient();
  const [input, setInput] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  // Addresses the gateway rejected on the last attempt, kept so a retry only
  // re-sends the failures rather than re-inviting everyone who succeeded.
  const [failed, setFailed] = useState<string[]>([]);
  const [sendError, setSendError] = useState<string | null>(null);

  const { valid, invalid } = parseEmails(input);

  const closeAndReset = () => {
    setInput("");
    setTouched(false);
    setBusy(false);
    setFailed([]);
    setSendError(null);
    onClose();
  };

  const submit = async () => {
    setTouched(true);
    if (valid.length === 0 || invalid.length > 0) return;

    setBusy(true);
    setSendError(null);
    // Send one invite per address; collect failures so a partial batch reports
    // exactly who didn't go through instead of silently dropping them.
    const rejected: string[] = [];
    let lastError: unknown;
    for (const email of valid) {
      try {
        await client.inviteMember(email);
        trackInviteMemberSent("memberInvite");
      } catch (err) {
        rejected.push(email);
        lastError = err;
      }
    }
    setBusy(false);

    if (rejected.length === 0) {
      onInvited();
      closeAndReset();
      return;
    }

    // Some sent: refresh the list for those, and leave the failures in the box.
    if (rejected.length < valid.length) onInvited();

    setFailed(rejected);
    setInput(rejected.join("\n"));
    setSendError(
      `Couldn't invite ${rejected.length} of ${valid.length}: ${friendlyError(lastError).message}`,
    );
  };

  const error =
    sendError ??
    (touched && invalid.length > 0
      ? `Not valid: ${invalid.join(", ")}`
      : touched && valid.length === 0
        ? "Enter at least one email address."
        : undefined);

  const count = valid.length;
  const sendLabel = busy
    ? "Sending…"
    : failed.length > 0
      ? `Retry ${count}`
      : count > 1
        ? `Send ${count} invitations`
        : "Send invitation";

  return (
    <TokenwiseModal
      open={open}
      title="Invite members"
      description="Paste one or more emails, separated by commas or new lines. Each person gets an email with a link to join your organization."
      busy={busy}
      onClose={closeAndReset}
      footer={
        <>
          <ModalButton disabled={busy} onClick={closeAndReset}>
            Cancel
          </ModalButton>
          <ModalButton
            variant="brand"
            disabled={busy || input.trim().length === 0}
            onClick={submit}
          >
            {sendLabel}
          </ModalButton>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="invite-emails"
            className="text-[9px] font-semibold uppercase tracking-widest text-[var(--label)]"
          >
            Emails
          </label>
          <textarea
            id="invite-emails"
            rows={4}
            autoFocus
            placeholder={"name@company.com\nteammate@company.com"}
            value={input}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? "invite-emails-message" : undefined}
            onChange={(e) => setInput(e.target.value)}
            className="w-full resize-y bg-transparent px-2.5 py-2 text-[13px] leading-relaxed text-[var(--text)] outline-none placeholder:text-[var(--text-muted)] disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              border: `1px solid ${error ? "var(--val-crimson)" : "var(--border-2)"}`,
            }}
          />
          <p
            id="invite-emails-message"
            className="text-[12px]"
            style={{
              color: error ? "var(--val-crimson)" : "var(--text-muted)",
            }}
          >
            {error ??
              (count > 0
                ? `${count} ${count === 1 ? "address" : "addresses"} ready to invite`
                : "Separate addresses with commas or new lines.")}
          </p>
        </div>
      </form>
    </TokenwiseModal>
  );
}
